import type { CursorPage } from '@dru/types';

/**
 * Follows a cursor to the end of a list.
 *
 * For the handful of screens that genuinely need every row — the partner
 * capability editor has to show the whole test catalogue grouped by category,
 * because an operator declaring what a venue can run is reading categories,
 * not searching for one test.
 *
 * The reason this exists rather than a bigger `limit`: those screens used to
 * call a list endpoint with no paging at all and render whatever came back.
 * Once the endpoint started paging, they would have silently rendered the
 * first page and looked complete — the same failure as the employee picker,
 * in a place where the consequence is a venue recorded as unable to run a test
 * it can.
 *
 * `maxPages` is a stop, not a page size. If a catalogue is ever large enough
 * to hit it, the screen using this needs a search box rather than a bigger
 * loop, and the throw says so instead of quietly truncating.
 */
export async function fetchAll<T>(
  page: (cursor: string | null) => Promise<CursorPage<T>>,
  maxPages = 10,
): Promise<T[]> {
  const out: T[] = [];
  let cursor: string | null = null;

  for (let i = 0; i < maxPages; i += 1) {
    const res: CursorPage<T> = await page(cursor);
    out.push(...res.items);
    if (!res.nextCursor) return out;
    cursor = res.nextCursor;
  }

  throw new Error(
    `This list is longer than ${maxPages} pages. The screen loading it needs a search, `
    + 'not a longer loop — showing a truncated list here would look complete and be wrong.',
  );
}
